import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Camera, AlertTriangle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Téma interfész
interface SiteTheme {
  id: number;
  name: string;
  // ... egyéb téma tulajdonságok
}

interface UserSettingsFormProps {
  initialEmail: string;
  initialUsername: string;
  initialAvatarUrl: string;
  initialAlias: string;
  initialshowAdultContent: boolean;
  initialshowShoppingOffers?: boolean;
  forcePasswordSetup: boolean;
  isOmniAuthUser: boolean;
  onSave: (data: {
    email: string;
    username: string;
    alias: string;
    currentPassword: string;
    newPassword?: string;
    showAdultContent: boolean;
    showShoppingOffers?: boolean;
  }) => void;
  onAvatarSave: (photoFile: File) => Promise<void>;
  onAutoSaveShoppingOffers?: (enabled: boolean) => void;
  onDeletePassword: () => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  themes: SiteTheme[];
  selectedThemeId: number | null;
  onThemeChange: (themeId: number) => void;
}

const UserSettingsForm: React.FC<UserSettingsFormProps> = ({
  initialEmail,
  initialUsername,
  initialAvatarUrl,
  initialAlias,
  initialshowAdultContent,
  initialshowShoppingOffers = true,
  isOmniAuthUser,
  forcePasswordSetup,
  onDeletePassword,
  onSave,
  onAvatarSave,
  onAutoSaveShoppingOffers,
  showError,
  showSuccess,
  themes,
  selectedThemeId,
  onThemeChange,
}) => {
  const { currentUser, logout } = useAuth();
  const [email, setEmail] = useState(initialEmail);
  const [username, setUsername] = useState(initialUsername);
  const [alias, setAlias] = useState(initialAlias);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(initialAvatarUrl);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [showAdultContent, setshowAdultContent] = useState(initialshowAdultContent);
  const [showShoppingOffers, setShowShoppingOffers] = useState(initialshowShoppingOffers);
  const [photoFile, setPhotoFile] = useState<File | undefined>(undefined);
  const [currentThemeId, setCurrentThemeId] = useState<number | null>(selectedThemeId);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAvatarSaving, setIsAvatarSaving] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const userName = currentUser?.translaterName || currentUser?.username;

  useEffect(() => {
    setEmail(initialEmail);
    setUsername(initialUsername);
    setAlias(initialAlias);
    setAvatarUrl(initialAvatarUrl);
    setshowAdultContent(initialshowAdultContent);
    setShowShoppingOffers(initialshowShoppingOffers ?? true);
    setCurrentThemeId(selectedThemeId);

    if (forcePasswordSetup && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }

  }, [initialEmail, initialUsername, initialAvatarUrl, initialAlias, initialshowAdultContent, initialshowShoppingOffers, forcePasswordSetup, selectedThemeId]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarApply = async () => {
    if (!photoFile) {
      return;
    }

    setIsAvatarSaving(true);
    try {
      await onAvatarSave(photoFile);
      setPhotoFile(undefined);
    } finally {
      setIsAvatarSaving(false);
    }
  };

  const getFirstValidChar = (name?: string): string => {
    if (!name) return 'U';
    const match = name.match(/[a-zA-Z]/);
    return match ? match[0].toUpperCase() : 'U';
  };
  const fallbackChar = getFirstValidChar(userName);

  const handleShoppingOffersChange = (enabled: boolean) => {
    setShowShoppingOffers(enabled);
    if (onAutoSaveShoppingOffers) {
      onAutoSaveShoppingOffers(enabled);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let hasError = false;

    if (forcePasswordSetup && !newPassword.trim()) {
      showError('A mentéshez kötelező jelszót beállítani!');
      hasError = true;
    }

    if (!email.trim()) {
      showError('Az email cím nem lehet üres!');
      hasError = true;
    }

    if (!username?.trim()) {
      showError('A felhasználónév nem lehet üres!');
      hasError = true;
    } else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username)) {
      showError('A felhasználónév nem lehet e-mail cím!');
      hasError = true;
    } else if (username.length > 20) {
      showError('A felhasználónév maximum 20 karakter lehet!');
      hasError = true;
    }

    if (!forcePasswordSetup && showPasswordFields) {
      if (!newPassword.trim() || !confirmNewPassword.trim()) {
        showError('Az új jelszó és a megerősítés mezők kitöltése kötelező a jelszó módosításához!');
        hasError = true;
      } else if (newPassword !== confirmNewPassword) {
        showError('Az új jelszó és a megerősítés nem egyezik!');
        hasError = true;
      } else if (newPassword.length < 6) {
        showError('Az új jelszó legalább 6 karakter hosszú legyen!');
        hasError = true;
      }
    }

    if (hasError) return;

    onSave({
      email,
      username,
      alias: alias || '',
      currentPassword: '',
      newPassword: showPasswordFields || forcePasswordSetup ? newPassword : undefined,
      showAdultContent,
      showShoppingOffers,
    });

    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setShowPasswordFields(false);
  };

  const handleThemeSelect = (themeId: string) => {
    const id = parseInt(themeId, 10);
    setCurrentThemeId(id);
    onThemeChange(id);
  };

  const handleDeleteAccount = async () => {
    // Validálás
    if (!deletePassword.trim()) {
      showError('Kérjük, add meg a jelenlegi jelszavadat.');
      return;
    }

    if (deleteConfirmation !== 'profil törlése') {
      showError('Kérjük, írd be pontosan: "profil törlése"');
      return;
    }

    setIsDeleting(true);

    try {
      const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || '';

      const response = await fetch('/api/settings/delete_account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          current_password: deletePassword,
          confirmation_text: deleteConfirmation,
        }),
      });

      console.log('Delete response:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('Delete success:', data);
        showSuccess('Profil sikeresen törölve. Azonnal kijelentkezünk...');

        // Kijelentkeztetjük a felhasználót
        logout();

        // Átirányítás a bejelentkezési oldalra
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
      } else {
        const result = await response.json();
        console.error('Delete error response:', result);
        showError(result.error || 'Hiba történt a profil törlése során.');
      }
    } catch (error) {
      console.error('Profil törlési hiba:', error);
      showError(`Hálózati hiba: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <Card className="bg-card text-card-foreground rounded-lg shadow-none border-none">
      <CardHeader>
        <CardTitle className="text-3xl font-bold mb-6 text-center text-primary">
          Felhasználói Beállítások
        </CardTitle>
      </CardHeader>      <CardContent className="space-y-8">      <div className="flex flex-col items-center gap-4">
        <Avatar className="w-24 h-24 border-4 border-primary">
          <AvatarImage src={avatarUrl} alt="Profilkép" className="object-cover" />
          <AvatarFallback className="bg-primary text-primary-foreground text-4xl">
            {fallbackChar}
          </AvatarFallback>
        </Avatar>
        <Label
          htmlFor="avatar-upload"
          className="cursor-pointer bg-secondary text-secondary-foreground hover:bg-muted hover:text-muted-foreground px-4 py-2 rounded-md flex items-center gap-2"
        >
          <Camera size={18} /> Profilkép módosítása
          <Input
            id="avatar-upload"
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            className="hidden"
          />
        </Label>
        {photoFile && (
          <Button
            type="button"
            onClick={handleAvatarApply}
            disabled={isAvatarSaving}
            className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground"
          >
            {isAvatarSaving ? 'Alkalmazás folyamatban...' : 'Kép alkalmazása'}
          </Button>
        )}
      </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-xl font-semibold text-foreground mb-4">Téma választás</h3>
          <div>
            <Label htmlFor="theme-select" className="text-muted-foreground">
              Weboldal téma
            </Label>
            <Select onValueChange={handleThemeSelect} value={currentThemeId?.toString()}>
              <SelectTrigger className="w-full mt-1 bg-secondary text-secondary-foreground border-border focus:border-primary focus:ring-primary">
                <SelectValue placeholder="Válassz egy témát..." />
              </SelectTrigger>
              <SelectContent>
                {themes.map((theme) => (
                  <SelectItem key={theme.id} value={theme.id.toString()}>
                    {theme.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-xl font-semibold text-foreground mb-4">Saját témáid</h3>
          <p className="text-muted-foreground mb-4">Itt hozhatsz létre és kezelhetsz saját témákat, amiket beküldhetsz felülvizsgálatra.</p>
          <Link to="/user/themes">
            <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white transition-colors">
              Témák szerkesztése / Beküldése
            </Button>
          </Link>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="border-t border-border pt-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">Email cím</h3>
            <div>
              <Label htmlFor="email" className="text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                readOnly
                className="mt-1 bg-secondary text-secondary-foreground border-border focus:border-primary focus:ring-primary"
              />
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">Felhasználónév</h3>
            {!username?.trim() && (
              <div className="bg-blue-500/20 border border-blue-600 text-blue-200 px-4 py-3 rounded-lg mb-4 text-center">
                <p className="font-bold">Felhasználónév szükséges</p>
                <p className="text-sm">Kérjük, add meg a felhasználóneved az alábbi mezőben!</p>
              </div>
            )}
            {username && username.length > 20 && (
              <div className="bg-red-500/20 border border-red-600 text-red-200 px-4 py-3 rounded-lg mb-4 text-center">
                <p className="font-bold">⚠️ Felhasználónév túl hosszú!</p>
                <p className="text-sm">A felhasználóneved {username.length} karakter, de maximum 20 karakterből állhat. Kérjük, rövidítsd meg!</p>
              </div>
            )}
            <div>
              <div className="flex justify-between items-end mb-2">
                <Label htmlFor="username" className="text-muted-foreground">
                  Felhasználónév
                </Label>
                <span className={`text-xs ${username && username.length > 20 ? 'text-red-400 font-bold' : 'text-muted-foreground'}`}>
                  max 20 karakter ({username?.length || 0}/20)
                </span>
              </div>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                maxLength={20}
                className={`mt-1 bg-secondary text-secondary-foreground border-border focus:border-primary focus:ring-primary ${username && username.length > 20 ? 'border-red-500' : ''}`}
              />
            </div>
          </div>

          {!forcePasswordSetup && !showPasswordFields ? (
            <div className="border-t border-border pt-6 text-center">
              <Button
                type="button"
                onClick={() => setShowPasswordFields(true)}
                className="bg-secondary text-secondary-foreground border-border hover:bg-muted hover:text-muted-foreground"
              >
                Jelszó módosítása
              </Button>
            </div>
          ) : (showPasswordFields || forcePasswordSetup) && (
            <div className="space-y-4 border-t border-border pt-6">
              {forcePasswordSetup && (
                <div className="bg-red-500/20 border border-red-600 text-red-200 px-4 py-3 rounded-lg mb-4 text-center">
                  <p className="font-bold text-lg">⚠️ Jelszó beállítása szükséges</p>
                  <p className="text-sm">A fiókod teljeskörű használatához, kérjük, állíts be egy jelszót. Addig nem használhatod az oldal többi részét.</p>
                </div>
              )}
              <h3 className="text-xl font-semibold text-foreground">{forcePasswordSetup ? 'Jelszó beállítása' : 'Jelszó módosítása'}</h3>
              <div>
                <Label htmlFor="new-password" className={forcePasswordSetup ? 'text-red-400 font-bold' : 'text-muted-foreground'}>
                  {forcePasswordSetup ? '★ Új jelszó (KÖTELEZŐ)' : 'Új jelszó'}
                </Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  ref={passwordInputRef}
                  className={`mt-1 bg-secondary text-secondary-foreground focus:border-primary focus:ring-primary ${forcePasswordSetup ? 'border-2 border-red-500 focus:border-red-400 focus:ring-red-500' : 'border-border'}`}
                  autoFocus={forcePasswordSetup}
                />
              </div>
              {!forcePasswordSetup && (
                <div>
                  <Label htmlFor="confirm-new-password" className="text-muted-foreground">
                    Új jelszó megerősítése
                  </Label>
                  <Input
                    id="confirm-new-password"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="mt-1 bg-secondary text-secondary-foreground border-border focus:border-primary focus:ring-primary"
                  />
                </div>
              )}
              {!forcePasswordSetup && <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPasswordFields(false);
                    setNewPassword('');
                    setConfirmNewPassword('');
                  }}
                  className="bg-muted text-muted-foreground border-border hover:bg-secondary hover:text-secondary-foreground"
                >
                  Mégsem
                </Button>
              </div>}
            </div>
          )}

          <div className="border-t border-border pt-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">Tartalom beállítások</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="shopping-offers" className="text-muted-foreground">
                  Játék vásárlási ajánlatok megjelenítése
                </Label>
                <Switch
                  id="shopping-offers"
                  checked={showShoppingOffers}
                  onCheckedChange={handleShoppingOffersChange}
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            Beállítások mentése
          </Button>
        </form>

        <div className="border-t border-border pt-6">
          <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <AlertTriangle size={20} className="text-red-500" /> Veszélyes zóna
          </h3>
          <p className="text-muted-foreground mb-4">
            A lenti gombbal véglegesen törölheted a profilodat.
          </p>
          <Button
            type="button"
            variant="destructive"
            onClick={() => setIsDeleteDialogOpen(true)}
            className="w-full bg-red-600 hover:bg-red-700 text-white transition-colors border border-red-500"
          >
            🗑️ Profil törlése
          </Button>
        </div>

      </CardContent>

      {/* Delete Account Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-card text-card-foreground border-border shadow-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-destructive flex items-center gap-2">
              <AlertTriangle size={24} /> Profil törlése
            </DialogTitle>
            <DialogDescription className="text-foreground text-base">
              Ez egy visszavonhatatlan cselekedet!
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-red-500/20 border border-red-600 text-red-200 px-4 py-4 rounded-lg space-y-3">
              <p className="font-bold">⚠️ Figyelem!</p>
              <p className="text-sm">
                A profil törlésével a profilod helyreállíthatatlan lesz. Technikai okokból a hozzászólások és a letöltési adataid megmaradnak, de minden egyéb adatod – a neved, e-mail címed, jelszavad – törlődik a rendszerből.
              </p>
              <p className="text-sm">
                A fiókodat egy "törölt-felhasználó" név fogja helyettesíteni.
              </p>
            </div>

            <div className="border-t border-border pt-4">
              <p className="text-foreground font-semibold mb-3">
                ⚠️ Kérjük, írd be, hogy "<strong>profil törlése</strong>" ha megértetted, hogy ez egy visszavonhatatlan cselekedet:
              </p>
              <Input
                type="text"
                placeholder="profil törlése"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                autoComplete="off"
                className="mt-2 bg-secondary text-secondary-foreground border-border focus:border-destructive focus:ring-destructive"
              />
            </div>

            <div>
              <Label htmlFor="delete-password" className="text-muted-foreground">
                Jelenlegi jelszó
              </Label>
              <Input
                id="delete-password"
                type="password"
                placeholder="Írd be a jelenlegi jelszavadat"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                autoComplete="new-password"
                className="mt-2 bg-secondary text-secondary-foreground border-border focus:border-destructive focus:ring-destructive"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDeletePassword('');
                setDeleteConfirmation('');
              }}
              className="flex-1 bg-muted text-muted-foreground border-border hover:bg-secondary hover:text-secondary-foreground"
              disabled={isDeleting}
            >
              Mégse
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={isDeleting || deleteConfirmation !== 'profil törlése'}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? 'Törlés folyamatban...' : '🗑️ Véglegesen törölni'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card >
  );
};

export default UserSettingsForm;

